
<div class="main-page">
<div class="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
  <div class="max-w-[1600px] mx-auto px-6 py-8">

    <!-- Header -->
    <div class="mb-8">
      <!-- Back Button & Title -->
      <div class="flex items-center gap-4 mb-6">
        <button
          (click)="goBack()"
          class="p-3 rounded-xl bg-white hover:bg-gray-100 shadow-md 
                 transition-all duration-300 group">
          <mat-icon class="text-gray-700 group-hover:-translate-x-1 transition-transform">
            arrow_back
          </mat-icon>
        </button>
        
        <div>
          <h1 class="text-4xl md:text-5xl font-black text-gray-900">
            All Stories
          </h1>
          <p class="text-gray-600 mt-2">
            Explore our complete collection of premium stories
          </p>
        </div>
      </div>

      <!-- Search & Filters Bar -->
      <div class="bg-white rounded-2xl shadow-lg p-6">
        
        <!-- Top Row: Search & View Toggle -->
        <div class="flex flex-col lg:flex-row gap-4 mb-4">
          
          <!-- Search -->
          <div class="relative flex-1">
            <mat-icon class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              search
            </mat-icon>
            <input
              type="text"
              [(ngModel)]="searchQuery"
              (ngModelChange)="onSearchChange()"
              placeholder="Search by title, author, or category..."
              class="w-full pl-12 pr-12 py-3 rounded-xl border-2 border-gray-200 
                     focus:border-blue-500 focus:outline-none focus:ring-2 
                     focus:ring-blue-100 transition-all duration-300
                     text-gray-900 placeholder-gray-400"
            />
            
            @if (searchQuery) {
              <button
                (click)="clearSearch()"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 
                       hover:text-gray-600 transition-colors">
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>

          <!-- View Mode Toggle -->
          <div class="flex gap-2 bg-gray-100 p-1 rounded-xl">
            <button
              (click)="viewMode = 'grid'"
              [class.bg-white]="viewMode === 'grid'"
              [class.shadow-md]="viewMode === 'grid'"
              [class.text-blue-600]="viewMode === 'grid'"
              class="px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2">
              <mat-icon>grid_view</mat-icon>
              <span class="hidden sm:inline font-semibold">Grid</span>
            </button>
            <button
              (click)="viewMode = 'list'"
              [class.bg-white]="viewMode === 'list'"
              [class.shadow-md]="viewMode === 'list'"
              [class.text-blue-600]="viewMode === 'list'"
              class="px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2">
              <mat-icon>view_list</mat-icon>
              <span class="hidden sm:inline font-semibold">List</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Info & Pagination Combined -->
    @if (!loading && filteredStories.length > 0) {
      <div class="mb-6">
        <!-- Results Summary -->
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <span class="font-medium text-gray-900 text-lg">{{ resultsText }}</span>
            @if (searchQuery) {
              <div class="text-sm text-gray-600 mt-1">
                Search results for "<strong class="text-gray-900">{{ searchQuery }}</strong>"
              </div>
            }
          </div>

          <!-- Items per page -->
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-600 font-medium">Show:</label>
            <select
              [(ngModel)]="itemsPerPage"
              (ngModelChange)="onFilterChange()"
              class="px-3 py-2 rounded-lg border-2 border-gray-200 
                     focus:border-blue-500 focus:outline-none text-sm
                     bg-white font-medium">
              <option [value]="12">12</option>
              <option [value]="24">24</option>
              <option [value]="48">48</option>
            </select>
          </div>
        </div>

      
      </div>
    }

    <!-- Loading Skeleton -->
    @if (loading) {
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        @for (i of [1,2,3,4,5,6,7,8]; track i) {
          <div class="animate-pulse rounded-2xl bg-white shadow-lg p-6">
            <div class="h-64 bg-gray-200 rounded-lg mb-4"></div>
            <div class="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        }
      </div>
    }

    <!-- Stories Grid/List -->
    @else {
      
      <!-- Grid View -->
      @if (viewMode === 'grid') {
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
             data-aos="fade-up">
          @for (story of displayedStories; track trackByStoryId($index, story)) {
            <article
              (click)="openStory(story)"
              class="group bg-white rounded-xl shadow-lg hover:shadow-2xl 
                     transition-all duration-500 cursor-pointer overflow-hidden"
              data-aos="fade-up"
              [attr.data-aos-delay]="($index % 8) * 50">

              <!-- Image -->
              <div class="relative h-64 overflow-hidden">
                <img
                  [src]="'https://epaper.all.tz/backend/uploads/media/' + story.thumbnail"
                  [alt]="story.title"
                  (error)="onImageError($event)"
                  class="h-full w-full object-cover transition-transform duration-700 
                         group-hover:scale-110"
                />
                
                <!-- Overlay -->
                <div class="absolute inset-0 bg-gradient-to-t 
                            from-black/70 via-black/20 to-transparent">
                </div>

                <!-- Category Badge -->
                @if (story.category) {
                  <span class="absolute top-4 left-4 px-3 py-1 text-xs font-semibold 
                               rounded-full bg-blue-600 text-white shadow-lg">
                    {{ story.category }}
                  </span>
                }

                <!-- Read Time Badge -->
                @if (story.readTime) {
                  <span class="absolute top-4 right-4 px-3 py-1 text-xs font-semibold 
                               rounded-full bg-white/90 text-gray-900 backdrop-blur-sm">
                    {{ story.readTime }} min
                  </span>
                }
              </div>

              <!-- Content -->
              <div class="p-5 space-y-3">
                <h3 class="text-lg font-bold text-gray-900 leading-snug 
                           line-clamp-2 min-h-[3.5rem] group-hover:text-blue-600 
                           transition-colors">
                  {{ story.title }}
                </h3>

                <p class="text-gray-600 text-sm line-clamp-2 min-h-[2.5rem]">
                  {{ story.subtitle }}
                </p>
              </div>
            </article>
          }
        </div>
      }

      <!-- List View -->
      @else {
        <div class="space-y-4" data-aos="fade-up">
          @for (story of displayedStories; track trackByStoryId($index, story)) {
            <article
              (click)="openStory(story)"
              class="group bg-white rounded-xl shadow-lg hover:shadow-2xl 
                     transition-all duration-500 cursor-pointer overflow-hidden
                     flex flex-col sm:flex-row"
              data-aos="fade-up"
              [attr.data-aos-delay]="$index * 30">

              <!-- Image -->
              <div class="relative w-full sm:w-80 h-64 sm:h-auto overflow-hidden flex-shrink-0">
                <img
                  [src]="'https://epaper.all.tz/backend/uploads/media/' + story.thumbnail"
                  [alt]="story.title"
                  (error)="onImageError($event)"
                  class="h-full w-full object-cover transition-transform duration-700 
                         group-hover:scale-110"
                />
                
                <!-- Category Badge -->
                @if (story.category) {
                  <span class="absolute top-4 left-4 px-3 py-1 text-xs font-semibold 
                               rounded-full bg-blue-600 text-white shadow-lg">
                    {{ story.category }}
                  </span>
                }
              </div>

              <!-- Content -->
              <div class="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <h3 class="text-2xl font-bold text-gray-900 leading-snug mb-3
                             group-hover:text-blue-600 transition-colors">
                    {{ story.title }}
                  </h3>

                  <p class="text-gray-600 line-clamp-3 mb-4">
                    {{ story.subtitle }}
                  </p>
                </div>

                <!-- Meta -->
                <div class="flex items-center justify-between text-sm text-gray-500">
                  <span class="font-medium">By {{ story.author }}</span>
                  <div class="flex items-center gap-4">
                    @if (story.readTime) {
                      <span class="flex items-center gap-1">
                        <mat-icon class="text-sm">schedule</mat-icon>
                        {{ story.readTime }} min
                      </span>
                    }
                  </div>
                </div>
              </div>
            </article>
          }
        </div>
      }

      
    }

  </div>
</div>
</div>